import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { EmbedLog } from '@data/models/audit.model';
import { AuditFilters, AuditPagination } from '@data/services/audit.service';
import * as moment from 'moment';
import { UserService } from '@data/services/user.service';
import { User } from '@data/models/user.model';
import { Observable, Subject, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

const MAX_DISPLAYED_UPDATED_FIELDS = 10;

@Component({
    selector: 'syn-audit-history',
    templateUrl: './audit-history.component.html',
    styleUrls: ['./audit-history.component.scss']
})
export class AuditHistoryComponent implements OnInit, OnDestroy {

    @Input() public logs: EmbedLog<'$who'>[];

    @Input() public title: string;

    @Input() public selectedLogId: string;

    @Input() public pagination: AuditPagination;

    @Input() public loading: boolean;

    @Output() public select = new EventEmitter<string>();

    @Output() public paginate = new EventEmitter<AuditPagination>();

    @Output() public filter = new EventEmitter<AuditFilters>();

    public readonly MAX_DISPLAYED_UPDATED_FIELDS = MAX_DISPLAYED_UPDATED_FIELDS;

    public updatedBy$: Observable<User[]>;

    public updatedByContentLoader = new Subject<{ term: string; strict: boolean }>();

    public filtersForm: FormGroup;

    public displayedColumns = [
        'updatedAt', 'updatedBy', 'updatedFields',
    ];

    readonly filterUpdatedFieldSeparatorKeysCodes = [ENTER, COMMA] as const;

    public updatedFields: string[] = [];

    private filtersFormChangesSubscription: Subscription;

    constructor(private formBuilder: FormBuilder,
        private userService: UserService) { }

    public ngOnInit(): void {
        this.filtersForm = this.formBuilder.group({
            range: this.formBuilder.group({
                from: undefined,
                to: undefined,
            }),
            updatedBy: this.formBuilder.control(undefined),
        });

        this.updatedBy$ = this.updatedByContentLoader.pipe(
            switchMap(({ term }) => {
                const safeTerm = term.replace(/"/g, '');
                return this.userService.findUsers({ filter: safeTerm, limit: 20 });
            })
        );

        this.filtersFormChangesSubscription = this.filtersForm.valueChanges
            .subscribe({
                next: () => this.sendFilters(),
                error: () => {}
            });
    }

    public ngOnDestroy(): void {
        if (this.filtersFormChangesSubscription) {
            this.filtersFormChangesSubscription.unsubscribe();
        }
    }

    public clearPickerRange(event: Event): void {
        event.stopPropagation();
        this.filtersForm.get('range').reset();
    }

    public addUpdatedField(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (value) {
            this.updatedFields.push(value);
            setTimeout(() => this.sendFilters());
        }

        event.chipInput!._blur(); // Required, Otherwise when the component is disabled an error occurs.
        event.chipInput!.clear();
    }

    public removeUpdatedField(updatedField: string): void {
        const index = this.updatedFields.indexOf(updatedField);

        if (index >= 0) {
            this.updatedFields.splice(index, 1);
            this.sendFilters();
        }
    }

    public selectLog(log: EmbedLog<'$who'>): void {
        this.select.emit(log.id);
    }

    public loadNextPage(): void {
        this.paginate.emit({
            page: this.pagination.page + 1,
            limit: this.pagination.limit,
        });
    }

    public onSubmit(event: Event): void {
        event.preventDefault();
    }

    private sendFilters(): void {
        const updatedBy: User | undefined = this.filtersForm.get('updatedBy').value;
        let { from, to } = this.filtersForm.get('range').value;

        if (from) {
            from = moment(from).utcOffset(0, true).hours(0).minutes(0).toDate();
        }

        if (to) {
            to = moment(to).utcOffset(0, true).hours(23).minutes(59).toDate();
        }

        this.filter.emit({
            updatedFields: [...this.updatedFields],
            range: { from, to },
            updatedBy: updatedBy?.id,
        });
    }
}
